import React from 'react';
import { PrintDataFields } from '../hooks/use-print-data';
import { DiagnosticVisuel } from './DiagnosticVisuel';
import { InfoField } from './InfoField';
import { RiskIndicator, mapRiskToEnglish, formatRiskLabel } from './RiskIndicator';
import { SectionHeader } from './SectionHeader';
import { BevaEstimateBlock } from './BevaEstimateBlock';
import { getPrintHeaderVariant } from '../utils';

interface PrintPageProps {
  data: PrintDataFields;
  inventaireId?: number;
  isMobile?: boolean;
}

export const PrintPage = ({ data, inventaireId, isMobile = false }: PrintPageProps) => {
  // Format BEVA value
  const formatBeva = () => {
    if (data.beva === '—' || !data.beva) return '—';
    const bevaNum = typeof data.beva === 'number'
      ? data.beva
      : parseFloat(String(data.beva));
    if (isNaN(bevaNum)) return `${data.beva} €`;
    return `${new Intl.NumberFormat('fr-FR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(bevaNum)} €`;
  };

  return (
    <>
      {/* Page 1 */}
      <div className={`page mx-auto bg-card ${!isMobile ? 'px-[200px] 2xl:px-[400px]' : 'px-4'}`}  >
        {/* Header */}
        <div className="flex flex-col sm:flex-row gap-4 sm:gap-6 mb-6">
          {data.organizationLogo ? (
            <img
              src={data.organizationLogo}
              alt={data.organizationName}
              className="w-[200px] max-h-[200px] h-auto shrink-0"
            />
          ) : (
            <div className="w-[200px] h-[110px] bg-muted flex items-center justify-center border border-border shrink-0">
              <span className="text-xs text-muted-foreground">Logo</span>
            </div>
          )}
          <div className="text-xs sm:text-sm">
            <p className="font-bold text-sm sm:text-base text-[#4E5155]">{data.organizationName}</p>
            <p>{data.organizationAddress}</p>
            <p>{data.organizationCity}</p>
            <p><span className="font-bold text-xs sm:text-sm text-[#4E5155]">SIRET :</span> {data.organizationSiret}</p>
            <p className="mt-2"><span className="font-bold text-xs sm:text-sm text-[#4E5155]">Recenseur :</span> {data.user?.prenom?.toUpperCase()} {data.user?.nom?.toUpperCase()}</p>
          </div>
        </div>

        {/* Main Title */}
        <SectionHeader variant={getPrintHeaderVariant(data.inventaireType)}>
          <span className="block sm:inline">FICHE DE SYNTHESE ARBRE </span>
          <span className="block sm:inline sm:before:content-['-'] sm:before:mx-2">{inventaireId}</span>
        </SectionHeader>

        {/* Tree Identification */}
        {isMobile ? (
          <div className="py-4 space-y-4 text-xs border-b border-border">
            {/* Mobile: Ordre optimisé pour lecture verticale */}
            <div>
              <InfoField label="Date" value={data.formattedDate} />
              <InfoField label="Espèce" value={data.especeLatin} />
              <InfoField label="Genre (latin)" value={data.genreLatin} />
              <InfoField label="Nom (FR)" value={data.especeLabel} />
              <InfoField label="Cultivar" value={data.cultivar} />

            </div>
            <div>
              <InfoField label="Adresse" value={data.addressLine} />
              <InfoField label="Coordonnées GPS" value={data.gps} />
            </div>
            <div>
              <InfoField label="Code" value={data.code_site} />
              <InfoField label="Identification" value={data.num_sujet} />
            </div>
            <div>



            </div>

          </div>
        ) : (
          <div className="py-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 text-xs border-b border-border">
            <div>
              <InfoField label="Date" value={data.formattedDate} />
              <InfoField label="Genre (latin)" value={data.genreLatin} />
              <InfoField label="Nom (FR)" value={data.especeLabel} />
              <InfoField label="Adresse" value={data.addressLine} />
            </div>
            <div>
              <InfoField label="Code" value={data.code_site} />
              <InfoField label="Espèce" value={data.especeLatin} />
            </div>
            <div className="sm:col-span-2 lg:col-span-1">
              <InfoField label="Identification" value={data.num_sujet} />
              <InfoField label="Cultivar" value={data.cultivar} />
              <InfoField label="Coordonnées GPS" value={data.gps} />
            </div>
          </div>
        )}

        {/* Caractéristiques */}
        <SectionHeader>Caractéristiques</SectionHeader>
        <div className="py-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 text-xs border-b border-border">
          <div className="border-l border-l-[0.5px] border-[#000] pl-2">
            <InfoField label="Hauteur en m" value={data.hauteur} />
            <InfoField label="Diamètre en cm" value={data.diametre} />
            <InfoField label="Âge estimé" value={data.age_estime} />
            <InfoField label="Stade de dév." value={data.caracteristiques.stade_dev} />
            <InfoField label="Remarquable" value={data.caracteristiques.critere} />
          </div>
          <div className="border-l border-l-[0.5px] border-[#000] pl-2">
            <InfoField label="Port" value={data.caracteristiques.port_arbre} />
            <InfoField label="Tronc" value={data.caracteristiques.caract_tronc} />
            <InfoField label="Pied de l'arbre" value={data.caracteristiques.caract_pied} />
          </div>
          <div className="border-l border-l-[0.5px] border-[#000] pl-2 sm:col-span-2 lg:col-span-1">
            <InfoField label="Implantation" value={data.caracteristiques.implantation} />
            <InfoField label="Domaine" value={data.caracteristiques.domaine} />
            <InfoField label="Situation" value={data.caracteristiques.situation} />
            <InfoField label="Accès" value={data.caracteristiques.accessibilite} />
          </div>
        </div>

        {/* Environnement */}
        <SectionHeader>Environnement</SectionHeader>
        <div className="py-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 text-xs border-b border-border">
          <div className="border-l border-l-[0.5px] border-[#000] pl-2">
            <InfoField label="Cohabitation" value={data.environnement.proximite} />
            <InfoField label="Demande de DICT" value={data.dictRequired} />
          </div>
          <div className="border-l border-l-[0.5px] border-[#000] pl-2">
            <InfoField label="Nuisance" value={data.environnement.nuisance} />
          </div>
          <div className="border-l border-l-[0.5px] border-[#000] pl-2 sm:col-span-2 lg:col-span-1">
            <InfoField label="Fréquentation" value={data.environnement.taux_freq} />
            <InfoField label="Type de Passage" value={data.environnement.type_passage} />
          </div>
        </div>

        {/* Diagnostic visuel */}
        <SectionHeader>Diagnostic visuel</SectionHeader>
        <div className="py-2 mb-2">
          <BevaEstimateBlock
            beva={data.beva}
            bevaDetail={data.bevaDetail}
            formatBevaValue={formatBeva}
          />
        </div>
        <div className="py-4">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-4">
            <div className="text-sm sm:text-base text-[#4E5155] font-semibold">Evaluation du risque</div>

            {/* Risk Table - Responsive */}
            <div className="overflow-x-auto">
              <table className="border border-gray-400 border-collapse min-w-full lg:min-w-0">
                <tbody>
                  <tr>
                    <td className="border border-gray-200 p-0">
                      <div className="h-6 sm:h-8 w-full sm:w-20 bg-[#10b981]" style={{ width: '100%' }} />
                    </td>
                    <td className="border border-gray-200 p-0">
                      <div className="h-6 sm:h-8 w-full sm:w-20 bg-[#facc15]" style={{ width: '100%' }} />
                    </td>
                    <td className="border border-gray-200 p-0">
                      <div className="h-6 sm:h-8 w-full sm:w-20 bg-[#ef4444]" style={{ width: '100%' }} />
                    </td>
                    <td className="border border-gray-200 p-0">
                      <div className="h-6 sm:h-8 w-full sm:w-20 bg-[#000000]" style={{ width: '100%' }} />
                    </td>
                  </tr>
                  <tr>
                    <td className="border border-gray-200 p-1 sm:p-2 text-center text-[10px] sm:text-xs">
                      Risque faible
                    </td>
                    <td className="border border-gray-200 p-1 sm:p-2 text-center text-[10px] sm:text-xs">
                      Risque modéré
                    </td>
                    <td className="border border-gray-200 p-1 sm:p-2 text-center text-[10px] sm:text-xs">
                      Risque élevé
                    </td>
                    <td className="border border-gray-200 p-1 sm:p-2 text-center text-[10px] sm:text-xs">
                      Risque imminent
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Tree Photos */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <DiagnosticVisuel formData={data} resolver={data.resolver} />
            {data.img1 ? (
              <div className="overflow-hidden border border-border">
                <img
                  src={data.img1}
                  alt="Vue de l'arbre 1"
                  className="w-full  sm:h-64 lg:h-[20rem] object-cover transition-transform duration-300 hover:scale-125 cursor-zoom-in"
                />
              </div>
            ) : (
              <div className="w-full h-48 sm:h-64 lg:h-[20rem] bg-white flex items-center justify-center text-xs text-muted-foreground"></div>
            )}
            {data.img2 ? (
              <div className="overflow-hidden border border-border">
                <img
                  src={data.img2}
                  alt="Vue de l'arbre 2"
                  className="w-full sm:h-64 lg:h-[20rem] object-cover transition-transform duration-300 hover:scale-125 cursor-zoom-in"
                />
              </div>
            ) : (
              <div className="w-full h-48 sm:h-64 lg:h-[20rem] bg-white flex items-center justify-center text-xs text-muted-foreground"></div>
            )}
            {data.img3 ? (
              <div className="overflow-hidden border border-border">
                <img
                  src={data.img3}
                  alt="Vue de l'arbre 3"
                  className="w-full sm:h-64 lg:h-[20rem] object-cover transition-transform duration-300 hover:scale-125 cursor-zoom-in"
                />
              </div>
            ) : (
              <div className="w-full h-48 sm:h-64 lg:h-[20rem] bg-white flex items-center justify-center text-xs text-muted-foreground"></div>
            )}
          </div>
        </div>
      </div>

      {/* Page 2 */}
      <div className={`page mx-auto bg-card ${!isMobile ? 'px-[200px] 2xl:px-[400px]' : 'px-4'}`}>
        {/* Diagnostic sanitaire */}
        <SectionHeader className="bg-gray-500">Diagnostic sanitaire</SectionHeader>

        {/* Table responsive - Horizontal scroll on mobile */}
        <div className="overflow-x-auto mt-4">
          <table className="w-full min-w-[600px] text-xs border-collapse border border-border">
            <thead>
              <tr>
                <th className="border border-border p-2 text-left w-1/6"></th>
                <th className="border border-border p-2 text-left w-2/6 text-xs sm:text-sm text-[#4E5155]">Défauts mécaniques</th>
                <th className="border border-border p-2 text-left w-2/6 text-xs sm:text-sm text-[#4E5155]">Préconisations de travaux</th>
                <th className="border border-border p-2 text-left w-1/6 text-xs sm:text-sm text-[#4E5155]">Risque de rupture</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="border border-border p-2 font-bold text-xs sm:text-sm text-[#4E5155]">Système racinaire</td>
                <td className="border border-border p-2 sm:p-3 text-xs sm:text-sm">{data.defautsMecaniques.collet}</td>
                <td className="border border-border p-2 sm:p-3 text-xs sm:text-sm">{data.travaux.travaux_collet}</td>
                <td className="border border-border p-2 sm:p-3">
                  <div className="flex items-center gap-2">
                    <span className="text-xs sm:text-sm">{formatRiskLabel(data.risque.collet)}</span>
                    <RiskIndicator level={mapRiskToEnglish(data.risque.collet)} />
                  </div>
                </td>
              </tr>
              <tr>
                <td className="border border-border p-2 font-bold text-xs sm:text-sm text-[#4E5155]">Tronc</td>
                <td className="border border-border p-2 sm:p-3 text-xs sm:text-sm">{data.defautsMecaniques.tronc}</td>
                <td className="border border-border p-2 sm:p-3 text-xs sm:text-sm">{data.travaux.travaux_tronc}</td>
                <td className="border border-border p-2 sm:p-3">
                  <div className="flex items-center gap-2">
                    <span className="text-xs sm:text-sm">{formatRiskLabel(data.risque.tronc)}</span>
                    <RiskIndicator level={mapRiskToEnglish(data.risque.tronc)} />
                  </div>
                </td>
              </tr>
              <tr>
                <td className="border border-border p-2 font-bold text-xs sm:text-sm text-[#4E5155]">Houppier</td>
                <td className="border border-border p-2 sm:p-3 text-xs sm:text-sm">{data.defautsMecaniques.houppier}</td>
                <td className="border border-border p-2 sm:p-3 text-xs sm:text-sm">{data.travaux.travaux_houppier}</td>
                <td className="border border-border p-2 sm:p-3">
                  <div className="flex items-center gap-2">
                    <span className="text-xs sm:text-sm">{formatRiskLabel(data.risque.houppier)}</span>
                    <RiskIndicator level={mapRiskToEnglish(data.risque.houppier)} />
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* État sanitaire global and Date prochaine visite */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-3">
          <div className="border border-gray-200">
            <table className="w-full text-xs border-collapse">
              <tbody>
                <tr>
                  <td className="border-b border-gray-200 p-2 font-bold text-xs sm:text-sm text-[#4E5155]">Etat sanitaire global</td>
                  <td className="border-b border-l border-gray-200 p-2 align-top">
                    <div className="flex flex-col break-words whitespace-normal">
                      <span className="p-2 text-xs sm:text-sm capitalize break-words">
                        {data.etatSanitaire.general}
                      </span>
                    </div>
                  </td>
                </tr>
                <tr>
                  <td className="p-2 font-bold text-xs sm:text-sm text-[#4E5155]">Risque de rupture général</td>
                  <td className="p-2 border-l">
                    <div className="flex items-center gap-2 p-2 sm:p-3">
                      <span className="text-xs sm:text-sm">{formatRiskLabel(data.risque.general)}</span>
                      <RiskIndicator level={mapRiskToEnglish(data.risque.general)} />
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="border border-gray-200">
            <table className="w-full text-xs border-collapse">
              <tbody>

                <tr>
                  <td className="border-b border-gray-200 p-2 sm:p-3 font-bold text-xs sm:text-sm text-[#4E5155]">Date d'intervention</td>
                  <td className="border-b border-l border-gray-200 p-2 sm:p-3 text-xs sm:text-sm">{data.travaux.date_travaux}</td>
                </tr>
                <tr>
                  <td className="border-b border-gray-200 p-2 sm:p-3 font-bold text-xs sm:text-sm text-[#4E5155]">Date de prochaine visite</td>
                  <td className="border-b border-l border-gray-200 p-2 sm:p-3 text-xs sm:text-sm">
                    {data.travaux.date_pro_visite}
                  </td>
                </tr>
                <tr>
                  <td className="p-2 sm:p-3 font-bold text-xs sm:text-sm text-[#4E5155]">DT/DICT requis</td>
                  <td className="p-2 sm:p-3 border-l text-xs sm:text-sm">{data.dictRequired}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* Commentaires */}
        <div className="mt-6 text-xs sm:text-sm">
          <span className="font-bold text-sm">Commentaires :</span>
          <div className="mt-2 text-muted-foreground break-words">{data.comments}</div>
        </div>
      </div>
    </>
  );
};